import { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import Sidebar from '../../dashboard/Sidebar';
import { useMyTeam } from '../../team/useMyTeam';
import { useIsStrategist } from '../hooks/useIsStrategist';
import { useOpenLiveSession } from '../hooks/useOpenLiveSession';
import {
  openSessionWindow,
  useStrategistLiveEntry,
} from '../hooks/useStrategistLiveEntry';
import { formatMapName } from '../lib/formatters';
import { buildLivePlayerPath, liveMatchOnSession } from '../lib/pickJoinTarget';
import LiveSessionEntryButton from './LiveSessionEntryButton';
import styles from './LiveLobbyPage.module.css';

export default function LiveLobbyPage() {
  const { user, logout } = useAuth();
  const { team, loading: teamLoading } = useMyTeam(user?.id);
  const isStrategist = useIsStrategist(team?.id);
  const liveEntry = useStrategistLiveEntry(team?.id);
  const { joinTarget, waitingForMatch, loading: sessionsLoading, error, refetch } = useOpenLiveSession(
    user?.id,
    team?.id,
  );
  const [signingOut, setSigningOut] = useState(false);

  const initials = user ? buildInitials(user.displayName || user.email) : '??';
  const teamName = team?.name?.trim() ? team.name : null;
  const teamInitials = teamName ? buildTeamInitials(teamName) : team ? '??' : null;

  const canJoin = joinTarget != null;
  // Hook only sets loading on the first fetch; polls do not re-enter loading.
  const loading = teamLoading || (Boolean(user?.id) && sessionsLoading);

  async function handleSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setSigningOut(false);
    }
  }

  function handleJoin() {
    if (!joinTarget) return;
    const path = buildLivePlayerPath(joinTarget);
    window.open(path, '_blank', 'noopener,noreferrer');
  }

  const { title, body, statusLive } = lobbyCopy({
    hasTeam: Boolean(team),
    loading,
    error,
    canJoin,
    waitingForMatch,
    map: joinTarget?.map,
  });

  return (
    <div className={styles.app}>
      <Sidebar />

      <div className={styles.main}>
        <header className={styles.topbar}>
          {team && (
            <>
              <div className={styles.teamCtx}>
                <span className={styles.teamCtxLogo}>{teamInitials}</span>
                <div>
                  <div className={styles.teamCtxLabel}>Active Team</div>
                  <div className={styles.teamCtxName}>{teamName ?? 'Unnamed team'}</div>
                </div>
              </div>
              <div className={styles.topbarDivider} aria-hidden="true" />
            </>
          )}

          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <span>Stratos</span>
            <span className={styles.sep}>/</span>
            <span className={styles.here}>Live</span>
          </nav>

          <div className={styles.spacer} />

          <LiveSessionEntryButton entry={liveEntry} />

          {user && (
            <div className={styles.user}>
              <span className={styles.avatar} aria-hidden="true">
                {initials}
              </span>
              <div>
                <div className={styles.userName}>{user.displayName}</div>
                <div className={styles.userEmail}>{user.email}</div>
              </div>
            </div>
          )}

          <button type="button" className={styles.signOut} onClick={handleSignOut} disabled={signingOut}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </header>

        <section className={styles.content}>
          <div className={styles.eyebrow}>Live session</div>
          <h1 className={styles.pageTitle}>Join the room</h1>
          <p className={styles.pageSubtitle}>
            {isStrategist
              ? 'Start a live session from the top bar, or open an existing one below.'
              : 'When your strategist opens a live session, it will appear below. Click join to participate.'}
          </p>

          <div className={styles.card}>
            <div className={styles.statusRow}>
              <span className={`${styles.statusDot} ${statusLive ? styles.live : ''}`} aria-hidden="true" />
              <span className={`${styles.statusLabel} ${statusLive ? styles.live : ''}`}>
                {statusLive ? 'Session available' : 'Standby'}
              </span>
            </div>

            <h2 className={styles.cardTitle}>{title}</h2>
            <p className={styles.cardBody}>{body}</p>

            {canJoin && joinTarget && (
              <div className={styles.meta}>Map · {formatMapName(joinTarget.map) || joinTarget.map}</div>
            )}

            <div className={styles.cardActions}>
              {isStrategist && liveEntry.mode !== 'hidden' ? (
                <button
                  type="button"
                  className={styles.joinBtn}
                  disabled={liveEntry.busy || liveEntry.sessionsLoading}
                  onClick={() => void liveEntry.run()}
                >
                  {liveEntry.busy ? 'Working…' : liveEntry.label}
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.joinBtn}
                  disabled={!canJoin}
                  aria-disabled={!canJoin}
                  title={
                    canJoin
                      ? 'Open live session in a new tab'
                      : waitingForMatch
                        ? 'Session is open but the match is not ready yet'
                        : 'No live session to join'
                  }
                  onClick={handleJoin}
                >
                  Join Session
                </button>
              )}
              {error && team && (
                <button type="button" className={styles.retryBtn} onClick={refetch}>
                  Retry
                </button>
              )}
            </div>
          </div>

          {isStrategist && (
            <div className={styles.sessionList}>
              <h2 className={styles.listTitle}>Open sessions</h2>
              {liveEntry.openSessions.length === 0 ? (
                <p className={styles.listEmpty}>No open sessions for this team.</p>
              ) : (
                <ul className={styles.list}>
                  {liveEntry.openSessions.map((session) => {
                    const live = liveMatchOnSession(session);
                    return (
                      <li key={session.id} className={styles.listRow}>
                        <div className={styles.listMain}>
                          <div className={styles.listId}>
                            Session · {session.id.slice(0, 8)}
                            {session.createdAt
                              ? ` · ${new Date(session.createdAt).toLocaleString()}`
                              : ''}
                          </div>
                          <div className={styles.listMeta}>
                            {live
                              ? `Live match · ${formatMapName(live.map) || live.map}`
                              : 'Waiting for match to start'}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={styles.listOpen}
                          onClick={() => openSessionWindow(session)}
                        >
                          Open
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function lobbyCopy({
  hasTeam,
  loading,
  error,
  canJoin,
  waitingForMatch,
  map,
}: {
  hasTeam: boolean;
  loading: boolean;
  error: Error | null;
  canJoin: boolean;
  waitingForMatch: boolean;
  map?: string;
}): { title: string; body: string; statusLive: boolean } {
  if (!hasTeam && !loading) {
    return {
      title: 'No team',
      body: 'Join a team to use Live sessions.',
      statusLive: false,
    };
  }
  if (loading) {
    return {
      title: 'Checking for live session…',
      body: 'Looking for an open session on your team.',
      statusLive: false,
    };
  }
  if (error) {
    return {
      title: 'Could not load sessions',
      body: error.message || 'Something went wrong while checking for a live session.',
      statusLive: false,
    };
  }
  if (canJoin) {
    return {
      title: 'Ready to join',
      body: `A live match is running${map ? ` on ${formatMapName(map) || map}` : ''}. Open the session in a new tab to get your actions.`,
      statusLive: true,
    };
  }
  if (waitingForMatch) {
    return {
      title: 'Session open — waiting for match',
      body: 'Your team has a live session, but no live match is ready yet. Join Session unlocks when the match starts.',
      statusLive: false,
    };
  }
  return {
    title: 'No live session',
    body: 'There is no open live session for your team right now. Check back when your strategist starts one.',
    statusLive: false,
  };
}

function buildInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function buildTeamInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return parts
    .slice(0, 3)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}
